import { useLayoutEffect, useState, type CSSProperties, type RefObject } from 'react';

export type Placement = 'bottom-start' | 'bottom-end' | 'bottom' | 'top-start' | 'top-end' | 'top';

export interface AnchorPosition {
  style: CSSProperties;
  /** The placement actually used after flipping at the viewport edge. */
  placement: Placement;
}

const VIEWPORT_MARGIN = 8;

function flip(placement: Placement): Placement {
  switch (placement) {
    case 'bottom-start':
      return 'top-start';
    case 'bottom-end':
      return 'top-end';
    case 'bottom':
      return 'top';
    case 'top-start':
      return 'bottom-start';
    case 'top-end':
      return 'bottom-end';
    case 'top':
      return 'bottom';
  }
}

function compute(anchor: DOMRect, float: DOMRect, placement: Placement, offset: number): CSSProperties {
  const vertical = placement.startsWith('top') ? 'top' : 'bottom';
  const align = placement.endsWith('-end') ? 'end' : placement.endsWith('-start') ? 'start' : 'center';
  let left =
    align === 'start'
      ? anchor.left
      : align === 'end'
        ? anchor.right - float.width
        : anchor.left + anchor.width / 2 - float.width / 2;
  const maxLeft = window.innerWidth - float.width - VIEWPORT_MARGIN;
  left = Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft));
  const top = vertical === 'bottom' ? anchor.bottom + offset : anchor.top - offset - float.height;
  return { position: 'fixed', top: Math.round(top), left: Math.round(left) };
}

/**
 * Positions a floating element (`floatRef`, `position: fixed`) next to an anchor. The placement flips
 * to the other side when the preferred side has no room; the horizontal position is clamped to the viewport.
 * Recomputes on scroll and resize while `active`.
 */
export function useAnchorPosition(
  anchorRef: RefObject<HTMLElement | null>,
  floatRef: RefObject<HTMLElement | null>,
  active: boolean,
  placement: Placement = 'bottom-start',
  offset = 6,
  /** Positioned ancestor the floating element is rendered into (then `position: absolute` relative to it). */
  container?: HTMLElement | null,
): AnchorPosition {
  const [state, setState] = useState<AnchorPosition>({
    style: { position: 'fixed', top: 0, left: 0 },
    placement,
  });

  useLayoutEffect(() => {
    if (!active) return;
    function update() {
      const anchor = anchorRef.current;
      const float = floatRef.current;
      if (!anchor || !float) return;
      const anchorRect = anchor.getBoundingClientRect();
      const floatRect = float.getBoundingClientRect();
      let used = placement;
      const roomBelow = window.innerHeight - anchorRect.bottom - offset - VIEWPORT_MARGIN;
      const roomAbove = anchorRect.top - offset - VIEWPORT_MARGIN;
      if (used.startsWith('bottom') && floatRect.height > roomBelow && roomAbove > roomBelow)
        used = flip(used);
      else if (used.startsWith('top') && floatRect.height > roomAbove && roomBelow > roomAbove)
        used = flip(used);
      const style = compute(anchorRect, floatRect, used, offset);
      if (container) {
        const box = container.getBoundingClientRect();
        style.position = 'absolute';
        style.top = Math.round(Number(style.top) - box.top - container.clientTop + container.scrollTop);
        style.left = Math.round(Number(style.left) - box.left - container.clientLeft + container.scrollLeft);
      }
      setState((previous) =>
        previous.placement === used && previous.style.top === style.top && previous.style.left === style.left
          ? previous
          : { style, placement: used },
      );
    }
    update();
    const observer = new ResizeObserver(update);
    if (floatRef.current) observer.observe(floatRef.current);
    if (anchorRef.current) observer.observe(anchorRef.current);
    window.addEventListener('resize', update);
    document.addEventListener('scroll', update, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      document.removeEventListener('scroll', update, true);
    };
  }, [active, anchorRef, container, floatRef, offset, placement]);

  return state;
}
